import { Injectable, inject } from '@angular/core'
import { Action, Store, select } from '@ngrx/store'

import { GbBaseFacade } from 'libs/shared/data-access-core/src/lib/+state/gb-base-facade-interface'
import * as PresenterWidgetsSelectors from './presenter-widgets.selectors'
import * as PresenterWidgetsActions from './presenter-widgets.actions'
import { WidgetModel, WidgetType } from './presenter-widgets.models'

@Injectable({
  providedIn: 'root',
})
export class PresenterWidgetsFacade implements GbBaseFacade {
  private readonly store = inject(Store)

  // Undo/Redo state
  canUndo$ = this.store.pipe(select(PresenterWidgetsSelectors.selectCanUndo))
  canRedo$ = this.store.pipe(select(PresenterWidgetsSelectors.selectCanRedo))

  // Selected Widget (across all widget types)
  selectedWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedWidget))
  selectedWidgetType$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedWidgetType))
  savingWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSavingWidget))

  // Countdown Widgets
  countdownWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectCountdownWidgetsLoaded))
  allCountdownWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllCountdownWidgetsList))
  selectedCountdownWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedCountdownWidget))
  countdownWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectCountdownWidgetsEditCreateLoaded),
  )
  countdownWidgetsCreateLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectCountdownWidgetsCreateLoaded))

  // Image Widgets
  imageWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectImageWidgetsLoaded))
  allImageWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllImageWidgetsList))
  selectedImageWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedImageWidget))
  imageWidgetsEditCreateLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectImageWidgetsEditCreateLoaded))
  imageWidgetsCreateLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectImageWidgetsCreateLoaded))

  // Text Widgets
  textWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectTextWidgetsLoaded))
  allTextWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllTextWidgetsList))
  selectedTextWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedTextWidget))
  textWidgetsEditCreateLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectTextWidgetsEditCreateLoaded))
  textWidgetsCreateLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectTextWidgetsCreateLoaded))

  // Smart Text Widgets
  smartTextWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSmartTextWidgetsLoaded))
  allSmartTextWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllSmartTextWidgetsList))
  selectedSmartTextWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedSmartTextWidget))
  smartTextWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectSmartTextWidgetsEditCreateLoaded),
  )
  smartTextWidgetsCreateLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSmartTextWidgetsCreateLoaded))

  // Total Raised Widgets
  totalRaisedWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectTotalRaisedWidgetsLoaded))
  allTotalRaisedWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllTotalRaisedWidgetsList))
  selectedTotalRaisedWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedTotalRaisedWidget))
  totalRaisedWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectTotalRaisedWidgetsEditCreateLoaded),
  )
  totalRaisedWidgetsCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectTotalRaisedWidgetsCreateLoaded),
  )

  // Team Scoring Widgets
  teamScoringWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectTeamScoringWidgetsLoaded))
  allTeamScoringWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllTeamScoringWidgetsList))
  selectedTeamScoringWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedTeamScoringWidget))
  teamScoringWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectTeamScoringWidgetsEditCreateLoaded),
  )
  teamScoringWidgetsCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectTeamScoringWidgetsCreateLoaded),
  )

  // Power Pledge Widgets
  powerPledgeWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectPowerPledgeWidgetsLoaded))
  allPowerPledgeWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllPowerPledgeWidgetsList))
  selectedPowerPledgeWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedPowerPledgeWidget))
  powerPledgeWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectPowerPledgeWidgetsEditCreateLoaded),
  )
  powerPledgeWidgetsCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectPowerPledgeWidgetsCreateLoaded),
  )

  // Notification Widgets
  notificationWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectNotificationWidgetsLoaded))
  allNotificationWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllNotificationWidgetsList))
  selectedNotificationWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedNotificationWidget))
  notificationWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectNotificationWidgetsEditCreateLoaded),
  )
  notificationWidgetsCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectNotificationWidgetsCreateLoaded),
  )

  // Item Table Widgets
  itemTableWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectItemTableWidgetsLoaded))
  allItemTableWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllItemTableWidgetsList))
  selectedItemTableWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedItemTableWidget))
  itemTableWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectItemTableWidgetsEditCreateLoaded),
  )
  itemTableWidgetsCreateLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectItemTableWidgetsCreateLoaded))

  // Raffle Winners Widgets
  raffleWinnersWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectRaffleWinnersWidgetsLoaded))
  allRaffleWinnersWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllRaffleWinnersWidgetsList))
  selectedRaffleWinnersWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedRaffleWinnersWidget))
  raffleWinnersWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectRaffleWinnersWidgetsEditCreateLoaded),
  )
  raffleWinnersWidgetsCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectRaffleWinnersWidgetsCreateLoaded),
  )

  // Honour Roll Widgets
  honourRollWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectHonourRollWidgetsLoaded))
  allHonourRollWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllHonourRollWidgetsList))
  selectedHonourRollWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedHonourRollWidget))
  honourRollWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectHonourRollWidgetsEditCreateLoaded),
  )
  honourRollWidgetsCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectHonourRollWidgetsCreateLoaded),
  )

  // Thermometer Widgets
  thermometerWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectThermometerWidgetsLoaded))
  allThermometerWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllThermometerWidgetsList))
  selectedThermometerWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedThermometerWidget))
  thermometerWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectThermometerWidgetsEditCreateLoaded),
  )
  thermometerWidgetsCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectThermometerWidgetsCreateLoaded),
  )

  // Raffle Draw Widgets
  raffleDrawWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectRaffleDrawWidgetsLoaded))
  allRaffleDrawWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllRaffleDrawWidgetsList))
  selectedRaffleDrawWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedRaffleDrawWidget))
  raffleDrawWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectRaffleDrawWidgetsEditCreateLoaded),
  )
  raffleDrawWidgetsCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectRaffleDrawWidgetsCreateLoaded),
  )

  // QR Code Widgets
  qrCodeWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectQrCodeWidgetsLoaded))
  allQrCodeWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllQrCodeWidgetsList))
  selectedQrCodeWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedQrCodeWidget))
  qrCodeWidgetsEditCreateLoaded$ = this.store.pipe(
    select(PresenterWidgetsSelectors.selectQrCodeWidgetsEditCreateLoaded),
  )
  qrCodeWidgetsCreateLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectQrCodeWidgetsCreateLoaded))

  // Video Widgets
  videoWidgetsLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectVideoWidgetsLoaded))
  allVideoWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllVideoWidgetsList))
  selectedVideoWidget$ = this.store.pipe(select(PresenterWidgetsSelectors.selectSelectedVideoWidget))
  videoWidgetsEditCreateLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectVideoWidgetsEditCreateLoaded))
  videoWidgetsCreateLoaded$ = this.store.pipe(select(PresenterWidgetsSelectors.selectVideoWidgetsCreateLoaded))

  allWidgets$ = this.store.pipe(select(PresenterWidgetsSelectors.selectAllWidgets))

  // Helper methods for selected widget management
  selectWidget(widget: WidgetModel, widgetType: WidgetType): void {
    this.store.dispatch(PresenterWidgetsActions.selectWidget({ widget, widgetType }))
  }

  clearSelectedWidget(): void {
    this.store.dispatch(PresenterWidgetsActions.clearSelectedWidget())
  }

  dispatch(action: Action): void {
    this.store.dispatch(action)
  }
}
